import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './DailyChallenges.css';

const DailyChallenges = ({ userId }) => {
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [userStreak, setUserStreak] = useState(0);
  const [completedToday, setCompletedToday] = useState(false);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadDailyChallenge = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/challenges/daily', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyChallenge(response.data.challenge);
      setCompletedToday(response.data.completedToday || false);
    } catch (err) {
      console.error('Error loading daily challenge:', err);
      setError('Failed to load daily challenge');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUserStreak = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/gamification/stats/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStreak(response.data.currentStreak || 0);
    } catch (err) {
      console.log('Streak info not available');
    }
  }, [userId]);

  useEffect(() => {
    loadDailyChallenge();
    loadUserStreak();
  }, [loadDailyChallenge, loadUserStreak]);

  const handleSubmit = async () => {
    if (!code.trim()) {
      setError('Please write some code first!');
      return;
    }

    setSubmitting(true);
    setError('');
    setOutput('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/api/challenges/daily/submit',
        {
          userId,
          code,
          language: dailyChallenge.language
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.passed) {
        setSuccess(`🎉 Challenge completed! Earned ${response.data.xpEarned} XP`);
        setCompletedToday(true);
        setUserStreak(response.data.newStreak);
        
        // Show confetti effect
        triggerConfetti();
      } else {
        setOutput(response.data.output);
        setError(response.data.message || 'Some test cases failed. Keep trying!');
      }
    } catch (err) {
      console.error('Error submitting challenge:', err);
      setError('Failed to submit solution');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerConfetti = () => {
    // Simple confetti effect
    const confettiContainer = document.querySelector('.confetti-container');
    if (confettiContainer) {
      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.animationDelay = Math.random() * 3 + 's';
        confetti.style.backgroundColor = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7'][Math.floor(Math.random() * 5)];
        confettiContainer.appendChild(confetti);
        setTimeout(() => confetti.remove(), 4000);
      }
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'difficulty-easy';
      case 'medium': return 'difficulty-medium';
      case 'hard': return 'difficulty-hard';
      default: return 'difficulty-medium';
    }
  };

  const getStreakMessage = () => {
    if (userStreak === 0) return "Start your streak today!";
    if (userStreak === 1) return "Great start! Keep it going!";
    if (userStreak < 7) return "Building momentum! 🔥";
    if (userStreak < 30) return "You're on fire! 🔥🔥";
    if (userStreak < 100) return "Unstoppable! 🔥🔥🔥";
    return "Legend status! 🏆🔥🔥🔥";
  };

  if (loading) {
    return (
      <div className="challenges-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading today's challenge...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="challenges-container">
      <div className="confetti-container"></div>
      
      {/* Header */}
      <div className="challenges-header">
        <div className="header-left">
          <h2>📅 Daily Challenge</h2>
          <p className="subtitle">Complete daily challenges to maintain your streak!</p>
        </div>
        <div className="streak-display">
          <div className="streak-icon">🔥</div>
          <div className="streak-info">
            <div className="streak-number">{userStreak}</div>
            <div className="streak-label">Day Streak</div>
          </div>
        </div>
      </div>

      {completedToday ? (
        <div className="completed-state">
          <div className="completed-icon">✅</div>
          <h3>Challenge Completed!</h3>
          <p>You've already completed today's challenge. Come back tomorrow for a new one!</p>
          <div className="streak-message">
            <span className="streak-flame">🔥</span>
            <span>{getStreakMessage()}</span>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{userStreak}</div>
              <div className="stat-label">Current Streak</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">+50</div>
              <div className="stat-label">XP Earned Today</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{userStreak * 25}</div>
              <div className="stat-label">Streak Bonus XP</div>
            </div>
          </div>
        </div>
      ) : dailyChallenge ? (
        <div className="challenge-content">
          {/* Challenge Info */}
          <div className="challenge-info">
            <div className="challenge-header">
              <h3>{dailyChallenge.title}</h3>
              <span className={`difficulty-badge ${getDifficultyColor(dailyChallenge.difficulty)}`}>
                {dailyChallenge.difficulty || 'Medium'}
              </span>
            </div>
            <p className="challenge-description">{dailyChallenge.description}</p>
            
            {/* Tags */}
            {dailyChallenge.tags && (
              <div className="tags-container">
                {dailyChallenge.tags.map((tag, idx) => (
                  <span key={idx} className="tag">{tag}</span>
                ))}
              </div>
            )}

            {/* Requirements */}
            <div className="requirements-section">
              <h4>📋 Requirements:</h4>
              <ul>
                {dailyChallenge.requirements?.map((req, idx) => (
                  <li key={idx}>{req}</li>
                )) || <li>Solve the problem correctly</li>}
              </ul>
            </div>

            {/* Examples */}
            {dailyChallenge.examples && (
              <div className="examples-section">
                <h4>💡 Examples:</h4>
                {dailyChallenge.examples.map((example, idx) => (
                  <div key={idx} className="example-box">
                    <div className="example-row">
                      <span className="example-label">Input:</span>
                      <code>{example.input}</code>
                    </div>
                    <div className="example-row">
                      <span className="example-label">Output:</span>
                      <code>{example.output}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rewards */}
            <div className="rewards-section">
              <h4>🎁 Rewards:</h4>
              <div className="rewards-grid">
                <div className="reward-card">
                  <span className="reward-icon">⭐</span>
                  <span className="reward-text">+50 XP</span>
                </div>
                <div className="reward-card">
                  <span className="reward-icon">🔥</span>
                  <span className="reward-text">+1 Streak</span>
                </div>
                {userStreak > 0 && (
                  <div className="reward-card bonus">
                    <span className="reward-icon">✨</span>
                    <span className="reward-text">+{userStreak * 5} Bonus XP</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Code Editor */}
          <div className="code-editor-section">
            <h4>💻 Your Solution:</h4>
            <textarea
              className="code-editor"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={`// Write your ${dailyChallenge.language || 'code'} solution here...\n\n`}
              spellCheck="false"
            />

            {/* Output */}
            {output && (
              <div className="output-section">
                <h4>📤 Output:</h4>
                <pre className="output-content">{output}</pre>
              </div>
            )}

            {/* Messages */}
            {error && (
              <div className="message-box error">
                <span className="message-icon">❌</span>
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="message-box success">
                <span className="message-icon">✅</span>
                <span>{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={submitting || completedToday}
            >
              {submitting ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Submit Solution</span>
                </>
              )}
            </button>

            {/* Hint */}
            <div className="hint-box">
              <span className="hint-icon">💡</span>
              <span>Tip: Test your code with the examples before submitting!</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Challenge Available</h3>
          <p>Check back later for today's challenge!</p>
        </div>
      )}
    </div>
  );
};

export default DailyChallenges;


